import React, {useCallback, useEffect, useState} from "react";
import {CURRENT_YEAR} from "../season";

export default function AdminTabsManager({
                                             enabledTabs,
                                             setEnabledTabs,
                                             setAdminMode,
                                             TABS,
                                             fetchWithToken,
                                             token,
                                             API_URL,
                                             setRefresh
                                         }) {
    const [savingTabs, setSavingTabs] = useState(false);
    const [tabsSaved, setTabsSaved] = useState(false);
    const [localTabs, setLocalTabs] = useState(enabledTabs);
    const [members, setMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(true);
    const [memberAction, setMemberAction] = useState(null);
    const [newPseudonym, setNewPseudonym] = useState("");
    const [memberError, setMemberError] = useState("");
    const [tabsError, setTabsError] = useState("");

    const loadMembers = useCallback(async () => {
        setLoadingMembers(true);
        setMemberError("");
        try {
            const data = await fetchWithToken(`${API_URL}/members?includeInactive=true`, token);
            if (Array.isArray(data)) {
                setMembers(data);
            } else {
                setMemberError(data?.error || "Impossible de charger les membres.");
            }
        } catch (error) {
            setMemberError("Impossible de charger les membres.");
        } finally {
            setLoadingMembers(false);
        }
    }, [API_URL, fetchWithToken, token]);

    useEffect(() => {
        loadMembers();
    }, [loadMembers]);

    const handleToggleTab = (key) => {
        setTabsSaved(false);
        setLocalTabs(tabs => (
            tabs.includes(key) ? tabs.filter(tab => tab !== key) : [...tabs, key]
        ));
    };

    const handleSaveTabs = async () => {
        setSavingTabs(true);
        setTabsSaved(false);
        setTabsError("");
        try {
            const data = await fetchWithToken(`${API_URL}/tabs-enabled`, token, {
                method: "PATCH",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({enabled: localTabs}),
            });
            if (data?.error) {
                setTabsError(data.error);
                return;
            }
            setEnabledTabs(localTabs);
            setTabsSaved(true);
        } catch (error) {
            setTabsError("Impossible d’enregistrer les modules.");
        } finally {
            setSavingTabs(false);
        }
    };

    const handleAddMember = async (event) => {
        event.preventDefault();
        const pseudonym = newPseudonym.trim();
        if (!pseudonym) return;

        setMemberAction("add");
        setMemberError("");
        try {
            const data = await fetchWithToken(`${API_URL}/members`, token, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({pseudonym}),
            });
            if (data?.error) {
                setMemberError(data.error);
                return;
            }
            setNewPseudonym("");
            await loadMembers();
            setRefresh(refresh => refresh + 1);
        } catch (error) {
            setMemberError("Impossible d’ajouter ce membre.");
        } finally {
            setMemberAction(null);
        }
    };

    const handleToggleMember = async (member) => {
        setMemberAction(member._id);
        setMemberError("");
        try {
            const data = await fetchWithToken(`${API_URL}/members/${member._id}`, token, {
                method: "PATCH",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({active: member.active === false}),
            });
            if (data?.error) {
                setMemberError(data.error);
                return;
            }
            await loadMembers();
            setRefresh(refresh => refresh + 1);
        } catch (error) {
            setMemberError("Impossible de modifier ce membre.");
        } finally {
            setMemberAction(null);
        }
    };

    const handleDeleteMember = async (member) => {
        if (!window.confirm(`Supprimer définitivement ${member.pseudonym} ?`)) return;

        setMemberAction(member._id);
        setMemberError("");
        try {
            const data = await fetchWithToken(`${API_URL}/members/${member._id}`, token, {method: "DELETE"});
            if (data?.error) {
                setMemberError(data.error);
                return;
            }
            await loadMembers();
            setRefresh(refresh => refresh + 1);
        } catch (error) {
            setMemberError("Impossible de supprimer ce membre.");
        } finally {
            setMemberAction(null);
        }
    };

    const closeAdmin = () => {
        setRefresh(refresh => refresh + 1);
        setAdminMode(false);
    };

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-8">
            <main className="mx-auto max-w-4xl bg-white p-6 shadow-lg">
                <header className="mb-8 flex flex-wrap items-center justify-between gap-4 border-b pb-5">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Administration</h1>
                        <div className="mt-1 text-sm text-gray-500">Famille et modules {CURRENT_YEAR}</div>
                    </div>
                    <button
                        type="button"
                        className="bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300"
                        onClick={closeAdmin}
                    >
                        Retour
                    </button>
                </header>

                <section className="border-b pb-8">
                    <h2 className="mb-4 text-lg font-bold text-gray-900">Modules disponibles</h2>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {TABS.map(tab => (
                            <label key={tab.key} className="flex items-center gap-3 border p-3">
                                <input
                                    type="checkbox"
                                    checked={localTabs.includes(tab.key)}
                                    onChange={() => handleToggleTab(tab.key)}
                                />
                                <span>{tab.label}</span>
                            </label>
                        ))}
                    </div>
                    <div className="mt-4 flex items-center gap-3">
                        <button
                            type="button"
                            className="bg-blue-700 px-4 py-2 text-white hover:bg-blue-800 disabled:opacity-50"
                            disabled={savingTabs}
                            onClick={handleSaveTabs}
                        >
                            {savingTabs ? "Enregistrement…" : "Enregistrer les modules"}
                        </button>
                        {tabsSaved && <span className="text-sm text-green-700">Enregistré</span>}
                    </div>
                    {tabsError && <div className="mt-3 text-sm text-red-700">{tabsError}</div>}
                </section>

                <section className="pt-8">
                    <h2 className="mb-4 text-lg font-bold text-gray-900">Membres de la famille</h2>
                    <form className="mb-6 flex flex-col gap-2 sm:flex-row" onSubmit={handleAddMember}>
                        <input
                            className="min-w-0 flex-1 border px-3 py-2"
                            placeholder="Prénom ou pseudonyme"
                            value={newPseudonym}
                            onChange={event => setNewPseudonym(event.target.value)}
                        />
                        <button
                            type="submit"
                            className="bg-green-700 px-4 py-2 text-white hover:bg-green-800 disabled:opacity-50"
                            disabled={memberAction === "add" || !newPseudonym.trim()}
                        >
                            Ajouter
                        </button>
                    </form>

                    {memberError && <div className="mb-4 border border-red-200 bg-red-50 p-3 text-red-700">{memberError}</div>}
                    {loadingMembers ? (
                        <div className="py-8 text-center text-gray-500">Chargement…</div>
                    ) : members.length === 0 ? (
                        <div className="py-8 text-center text-gray-500">Aucun membre pour {CURRENT_YEAR}.</div>
                    ) : (
                        <div className="divide-y border-y">
                            {members.map(member => {
                                const isActive = member.active !== false;
                                const isBusy = memberAction === member._id;
                                return (
                                    <div key={member._id} className="flex flex-wrap items-center gap-3 py-3">
                                        <div className="min-w-0 flex-1">
                                            <div className={`font-medium ${isActive ? "text-gray-900" : "text-gray-400"}`}>
                                                {member.pseudonym}
                                            </div>
                                            <div className={`text-xs ${isActive ? "text-green-700" : "text-gray-500"}`}>
                                                {isActive ? "Actif" : "Désactivé"}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="bg-gray-200 px-3 py-2 text-sm text-gray-800 hover:bg-gray-300 disabled:opacity-50"
                                            disabled={isBusy}
                                            onClick={() => handleToggleMember(member)}
                                        >
                                            {isActive ? "Désactiver" : "Activer"}
                                        </button>
                                        <button
                                            type="button"
                                            className="bg-red-100 px-3 py-2 text-sm text-red-700 hover:bg-red-200 disabled:opacity-50"
                                            disabled={isBusy}
                                            onClick={() => handleDeleteMember(member)}
                                        >
                                            Supprimer
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
}
